import React from 'react';
import { ReceiptDto } from '../../types/receipt';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { PrinterIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface ReceiptViewerProps {
  receipt: ReceiptDto;
  onPrint?: (id: string) => void;
  onDownload?: (id: string) => void;
  className?: string;
}

export const ReceiptViewer: React.FC<ReceiptViewerProps> = ({
  receipt,
  onPrint,
  onDownload,
  className = '',
}) => {
  const { payment } = receipt || {};
  // Safely access client with proper type
  const client: Client = payment?.client || { id: '', firstName: '', lastName: '' };
  
  // Format the issued date, handling both string and Date objects
  const formatIssuedDate = () => {
    if (!receipt.issuedAt) return 'N/A';
    try {
      let dateValue: Date;
      const issuedAt = receipt.issuedAt as any; // Type assertion to handle different input types
      
      if (typeof issuedAt === 'string') {
        dateValue = new Date(issuedAt);
      } else if (issuedAt instanceof Date) {
        dateValue = issuedAt;
      } else if (typeof issuedAt === 'number') {
        dateValue = new Date(issuedAt);
      } else {
        // Fallback for any other case
        dateValue = new Date(String(issuedAt));
      }
      
      // Ensure we have a valid date
      if (isNaN(dateValue.getTime())) {
        return 'Invalid date';
      }
      
      return formatDate(dateValue.toISOString());
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };
  
  // Format the amount with currency
  const formatAmount = () => {
    if (payment?.amount == null) return 'N/A';
    try {
      return formatCurrency(payment.amount, payment.currency || 'USD');
    } catch (error) {
      console.error('Error formatting amount:', error);
      return 'Invalid amount';
    }
  };
  
  // Handle case where receipt or payment might be missing
  if (!receipt || !payment) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
        <p>No receipt information available</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Receipt</h2>
            <p className="text-sm text-gray-500">#{receipt.receiptNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Issued on</p>
            <p className="font-medium">
              {formatIssuedDate()}
            </p>
          </div>
        </div>

        {/* Client Info */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Bill To</h3>
          <p className="font-medium">
            {client?.firstName || 'Unknown'} {client?.lastName || 'Customer'}
          </p>
          {client?.email && <p className="text-sm text-gray-600">{client.email}</p>}
        </div>

        {/* Payment Details */}
        <div className="border-t border-b border-gray-200 py-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Payment Method</p>
              <p className="font-medium capitalize">
              {payment.method ? String(payment.method).toLowerCase() : 'N/A'}
            </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Payment Status</p>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  payment.status === 'COMPLETED'
                    ? 'bg-green-100 text-green-800'
                    : payment.status === 'PENDING'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {payment.status}
              </span>
            </div>
          </div>
        </div>

        {/* Amount */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-lg font-medium">Amount Paid</p>
          <p className="text-2xl font-bold">
            {formatAmount()}
          </p>
        </div>

        {/* Notes */}
        {receipt.notes && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
            <p className="text-sm text-gray-700">{receipt.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 flex justify-end space-x-3">
          {onDownload && (
            <button
              type="button"
              onClick={() => onDownload(receipt.id)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
              Download
            </button>
          )}
          {onPrint && (
            <button
              type="button"
              onClick={() => onPrint(receipt.id)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PrinterIcon className="-ml-1 mr-2 h-5 w-5" />
              Print Receipt
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptViewer;
